import { useEffect, useState } from 'react';
import type { GroupingLevel } from '../types/GroupingLevel';
import { getGroupingLevels } from '../api/groupingLevels';

// Module-level cache shared by every hook caller for the lifetime of the page.
// `inflight` deduplicates concurrent fetches; cleared on failure so the next
// mount retries. Fetched LAZILY — only when this hook first runs (i.e. when
// CategoriesPage mounts); never part of any login/startup preload. Cleared on
// logout via clearGroupingLevelsCache() — next mount re-fetches.
let cache: GroupingLevel[] | null = null;
let inflight: Promise<GroupingLevel[]> | null = null;

function loadGroupingLevels(): Promise<GroupingLevel[]> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = getGroupingLevels()
    .then((data) => {
      cache = data;
      inflight = null;
      return data;
    })
    .catch((err: unknown) => {
      inflight = null;
      throw err;
    });
  return inflight;
}

export function clearGroupingLevelsCache(): void {
  cache = null;
  inflight = null;
}

interface UseGroupingLevelsResult {
  data: GroupingLevel[] | null;
  loading: boolean;
  error: string | null;
}

export function useGroupingLevels(): UseGroupingLevelsResult {
  const [state, setState] = useState<UseGroupingLevelsResult>(() =>
    cache
      ? { data: cache, loading: false, error: null }
      : { data: null, loading: true, error: null },
  );

  useEffect(() => {
    if (cache) {
      // Cache may have populated between render and effect (e.g. a sibling
      // instance finished fetching). Adopt it; bail if state already matches.
      setState((prev) =>
        prev.data === cache
          ? prev
          : { data: cache, loading: false, error: null },
      );
      return;
    }

    let cancelled = false;
    loadGroupingLevels()
      .then((data) => {
        if (cancelled) return;
        setState({ data, loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : String(err);
        setState({ data: null, loading: false, error: message });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
